import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
    Typography,
    Sheet,
    Card,
    CardContent,
    Divider,
    Grid,
    Box,
    Chip,
    Button,
    Select,
    Option,
    Stack,
    Alert,
    CircularProgress,
    Link,
    Modal,
    ModalDialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    Dropdown,
    MenuButton,
    ListItemDecorator,
    Input,
    FormControl,
    FormLabel,
    Textarea,
} from '@mui/joy';
import {
    Add,
    Download,
    CheckCircle,
    Cancel,
    Schedule,
    Warning,
    Close,
    ArrowBack,
    Memory,
    Delete,
    MoreVert,
    Check,
    Block,
    AssignmentInd,
    Layers,
    Visibility,
} from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_ADMIN_API_BASE || 'http://localhost:9300';

const FirmwarePage = () => {
    const { session, profile } = useAuth();
    const navigate = useNavigate();
    const [firmwareReleases, setFirmwareReleases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deviceKind, setDeviceKind] = useState('hub-v1');
    const [approvalStatus, setApprovalStatus] = useState('all');
    const [openNotesId, setOpenNotesId] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [releaseToDelete, setReleaseToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [deleteSuccess, setDeleteSuccess] = useState('');
    const [actionMenuOpen, setActionMenuOpen] = useState(null); // Track which card's menu is open
    const [reassignModalOpen, setReassignModalOpen] = useState(false);
    const [releaseToReassign, setReleaseToReassign] = useState(null);
    const [approvers, setApprovers] = useState([]);
    const [loadingApprovers, setLoadingApprovers] = useState(false);
    const [reassignError, setReassignError] = useState('');
    const [reassigning, setReassigning] = useState(false);
    
    // Add Release Candidate modal state
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [newVersion, setNewVersion] = useState('');
    const [newReleaseNotes, setNewReleaseNotes] = useState('');
    const [newDeviceKind, setNewDeviceKind] = useState('hub-v1');
    const [selectedApproverId, setSelectedApproverId] = useState('');
    const [newComposeFile, setNewComposeFile] = useState('');
    const [addError, setAddError] = useState('');
    const [addSuccess, setAddSuccess] = useState('');
    const [adding, setAdding] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [viewComposeRelease, setViewComposeRelease] = useState(null);
    
    const loadFirmwareReleases = async () => {
        try {
            setLoading(true);
            setError(null);
    
            // Build query parameters, skipping "all" values
            const params = new URLSearchParams();
            
            // Always include device_kind since we removed "all" option
            params.append('device_kind', deviceKind);
            
            if (approvalStatus !== 'all') {
                const approvedOnly = approvalStatus === 'approved';
                params.append('approved_only', approvedOnly.toString());
            }
            
            const url = `${API_BASE}/firmware/releases${params.toString() ? '?' + params.toString() : ''}`;
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': session?.accessToken,
                    'Vendor-Id': session?.vendorId,
                    'X-Service-Name': 'admin-web-client',
                    'Content-Type': 'application/json'
                }
            });
    
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
    
            const data = await response.json();
            setFirmwareReleases(data);
        } catch (err) {
            console.error('Failed to load firmware releases:', err);
            setError('Failed to load firmware releases. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadFirmwareReleases();
    }, [deviceKind, approvalStatus]);

    const getApprovalStatusColor = (status) => {
        switch (status) {
            case 'approved':
                return { color: 'success', variant: 'soft' };
            case 'rejected':
                return { color: 'danger', variant: 'soft' };
            case 'pending':
                return { color: 'warning', variant: 'soft' };
            default:
                return { color: 'neutral', variant: 'soft' };
        }
    };

    const getApprovalStatusIcon = (status) => {
        switch (status) {
            case 'approved':
                return <CheckCircle />;
            case 'rejected':
                return <Cancel />;
            case 'pending':
                return <Schedule />;
            default:
                return <Warning />;
        }
    };

    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        const dateOptions = { 
            day: 'numeric', 
            month: 'short', 
            year: 'numeric' 
        };
        const timeOptions = { 
            hour: '2-digit', 
            minute: '2-digit', 
            hour12: false 
        };
        
        const formattedDate = date.toLocaleDateString('en-US', dateOptions);
        const formattedTime = date.toLocaleTimeString('en-US', timeOptions);
        
        return `${formattedDate}, ${formattedTime}`;
    };

    const handleAddReleaseCandidate = () => {
        setAddModalOpen(true);
        setAddError('');
        setAddSuccess('');
        setValidationErrors({});
        setNewVersion('');
        setNewReleaseNotes('');
        setNewDeviceKind('hub-v1');
        setSelectedApproverId('');
        loadApprovers();
    };

    const handleCloseAddModal = () => {
        setAddModalOpen(false);
        setAddError('');
        setAddSuccess('');
        setValidationErrors({});
        setNewVersion('');
        setNewReleaseNotes('');
        setNewComposeFile('');
        setSelectedApproverId('');
        setAdding(false);
        setApprovers([]);
    };

    const validateAddForm = () => {
        const errors = {};
        
        if (!newVersion.trim()) {
            errors.version = 'Firmware version is required';
        }
        
        if (!newReleaseNotes.trim()) {
            errors.releaseNotes = 'Release notes are required';
        }
        
        if (!selectedApproverId) {
            errors.approver = 'Approver selection is required';
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateReleaseCandidate = async () => {
        if (!validateAddForm()) {
            return;
        }

        try {
            setAdding(true);
            setAddError('');
            setAddSuccess('');

            const payload = {
                version_name: newVersion.trim(),
                device_kind: newDeviceKind,
                release_notes: newReleaseNotes.trim(),
                created_by_id: profile?.id,
                approver_id: parseInt(selectedApproverId),
                ...(newComposeFile.trim() && { compose_file: newComposeFile.trim() }),
            };

            const response = await fetch(`${API_BASE}/firmware/releases`, {
                method: 'POST',
                headers: {
                    'Authorization': session?.accessToken,
                    'Vendor-ID': session?.vendorId,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`Failed to create release candidate: ${response.status} ${errorData}`);
            }

            const result = await response.json();
            setAddSuccess('Release candidate created successfully!');
            
            // Reload firmware list to show the new release
            loadFirmwareReleases();
            
            // Close modal after short delay
            setTimeout(() => {
                handleCloseAddModal();
            }, 1500);

        } catch (err) {
            console.error('Failed to create release candidate:', err);
            setAddError(err.message || 'Failed to create release candidate. Please try again.');
        } finally {
            setAdding(false);
        }
    };

    const handleDeleteRelease = async () => {
        if (!releaseToDelete) return;

        try {
            setDeleting(true);
            setDeleteError('');
            setDeleteSuccess('');

            const response = await fetch(`${API_BASE}/firmware/releases/${releaseToDelete.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': session?.accessToken,
                    'Vendor-ID': session?.vendorId,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`Failed to delete release: ${response.status} ${errorData}`);
            }

            setDeleteSuccess(`Release ${releaseToDelete.version_name} deleted successfully!`);
            
            // Remove the deleted release from the list
            setFirmwareReleases(prev => prev.filter(release => release.id !== releaseToDelete.id));
            
            // Close modal after short delay
            setTimeout(() => {
                setDeleteModalOpen(false);
                setDeleteSuccess('');
                setReleaseToDelete(null);
            }, 1500);

        } catch (err) {
            console.error('Failed to delete release:', err);
            setDeleteError(err.message || 'Failed to delete release. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    const handleOpenDeleteModal = (release) => {
        setReleaseToDelete(release);
        setDeleteModalOpen(true);
        setDeleteError('');
        setDeleteSuccess('');
    };

    const handleCloseDeleteModal = () => {
        setDeleteModalOpen(false);
        setReleaseToDelete(null);
        setDeleteError('');
        setDeleteSuccess('');
    };

    const handleApproveRelease = async (release) => {
        try {
            const response = await fetch(`${API_BASE}/firmware/releases/${release.id}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': session?.accessToken,
                    'Vendor-ID': session?.vendorId,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ new_status: 'approved' })
            });

            if (!response.ok) {
                throw new Error(`Failed to approve release: ${response.status}`);
            }

            // Update the release status in the local state
            setFirmwareReleases(prev => prev.map(r => 
                r.id === release.id ? { ...r, approval_status: 'approved' } : r
            ));

        } catch (err) {
            console.error('Failed to approve release:', err);
            setError(`Failed to approve release: ${err.message}`);
            setTimeout(() => setError(null), 5000);
        }
    };

    const handleRejectRelease = async (release) => {
        try {
            const response = await fetch(`${API_BASE}/firmware/releases/${release.id}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': session?.accessToken,
                    'Vendor-ID': session?.vendorId,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ new_status: 'rejected' })
            });

            if (!response.ok) {
                throw new Error(`Failed to reject release: ${response.status}`);
            }

            // Update the release status in the local state
            setFirmwareReleases(prev => prev.map(r => 
                r.id === release.id ? { ...r, approval_status: 'rejected' } : r
            ));

        } catch (err) {
            console.error('Failed to reject release:', err);
            setError(`Failed to reject release: ${err.message}`);
            setTimeout(() => setError(null), 5000);
        }
    };

    const handlePendingRelease = async (release) => {
        try {
            const response = await fetch(`${API_BASE}/firmware/releases/${release.id}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': session?.accessToken,
                    'Vendor-ID': session?.vendorId,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ new_status: 'pending' })
            });

            if (!response.ok) {
                throw new Error(`Failed to set release to pending: ${response.status}`);
            }

            // Update the release status in the local state
            setFirmwareReleases(prev => prev.map(r => 
                r.id === release.id ? { ...r, approval_status: 'pending' } : r
            ));

        } catch (err) {
            console.error('Failed to set release to pending:', err);
            setError(`Failed to set release to pending: ${err.message}`);
            setTimeout(() => setError(null), 5000);
        }
    };

    // Check if current admin can approve/reject this release
    const canModifyApproval = (release) => {
        const currentAdminId = profile?.id;
        return release.approver?.id && currentAdminId && 
               (currentAdminId == release.approver.id);
    };

    // Check if current admin can reassign this release (approver or creator)
    const canReassign = (release) => {
        const currentAdminId = profile?.id;
        return currentAdminId && (
            (release.approver?.id && currentAdminId == release.approver.id) ||
            (release.creator?.id && currentAdminId == release.creator.id)
        );
    };

    const loadApprovers = async () => {
        try {
            setLoadingApprovers(true);
            setReassignError('');

            const response = await fetch(`${API_BASE}/firmware/approvers`, {
                headers: {
                    'Authorization': session?.accessToken,
                    'Vendor-ID': session?.vendorId,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to load approvers: ${response.status}`);
            }

            const approversData = await response.json();
            setApprovers(approversData);
        } catch (err) {
            console.error('Failed to load approvers:', err);
            setReassignError(`Failed to load approvers: ${err.message}`);
        } finally {
            setLoadingApprovers(false);
        }
    };

    const handleOpenReassignModal = (release) => {
        setReleaseToReassign(release);
        setReassignModalOpen(true);
        setReassignError('');
        loadApprovers();
    };

    const handleCloseReassignModal = () => {
        setReassignModalOpen(false);
        setReleaseToReassign(null);
        setApprovers([]);
        setReassignError('');
        setReassigning(false);
    };

    const handleSelectApprover = async (approverId) => {
        if (!releaseToReassign || reassigning) return;

        try {
            setReassigning(true);
            setReassignError('');

            const response = await fetch(`${API_BASE}/firmware/releases/${releaseToReassign.id}/approver`, {
                method: 'PATCH',
                headers: {
                    'Authorization': session?.accessToken,
                    'Vendor-ID': session?.vendorId,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ approver_id: approverId })
            });

            if (!response.ok) {
                throw new Error(`Failed to reassign release: ${response.status}`);
            }

            const updatedRelease = await response.json();
            
            // Update the release in the local state
            setFirmwareReleases(prev => prev.map(r => 
                r.id === releaseToReassign.id ? updatedRelease : r
            ));

            // Close modal after successful reassignment
            handleCloseReassignModal();

        } catch (err) {
            console.error('Failed to reassign release:', err);
            setReassignError(`Failed to reassign release: ${err.message}`);
        } finally {
            setReassigning(false);
        }
    };

    if (loading) {
        return (
            <Sheet sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <Stack spacing={2} alignItems="center">
                    <CircularProgress size="lg" />
                    <Typography level="body-lg">Loading firmware releases...</Typography>
                </Stack>
            </Sheet>
        );
    }

    if (error) {
        return (
            <Sheet sx={{ p: 4, maxWidth: '1400px', mx: 'auto' }}>
                <Alert color="danger" variant="soft" sx={{ mb: 3 }}>
                    <Typography>{error}</Typography>
                </Alert>
                <Typography level="body-sm" textColor="neutral.500">
                    Please try refreshing the page or contact support if the problem persists.
                </Typography>
            </Sheet>
        );
    }

    return (
        <Sheet sx={{ p: 4, maxWidth: '1400px', mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <IconButton 
                        variant="outlined" 
                        onClick={() => navigate('/admin')}
                        sx={{ borderRadius: '50%' }}
                    >
                        <ArrowBack />
                    </IconButton>
                    <Memory sx={{ fontSize: 32, color: 'danger.500' }} />
                    <Box>
                        <Typography level="h2" sx={{ mb: 0.5 }}>
                    Firmware Management
                </Typography>
                        <Typography level="body-sm" textColor="neutral.500">
                            Manage firmware releases, approvals and deployments
                        </Typography>
                    </Box>
                </Stack>
                <Stack direction="row" justifyContent="flex-end">
                <Button
                    startDecorator={<Add />}
                    onClick={handleAddReleaseCandidate}
                    variant="solid"
                    color="primary"
                >
                    Add Release Candidate
                </Button>
            </Stack>
            </Box>

            {/* Filters */}
            <Card variant="outlined" sx={{ mb: 4 }}>
                <CardContent>
                    <Typography level="h4" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ 
                            width: 40, 
                            height: 40, 
                            borderRadius: '50%', 
                            bgcolor: 'neutral.100', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            mr: 2 
                        }}>
                            🔍
                        </Box>
                        Filters
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid xs={12} sm={6}>
                            <Typography level="title-sm" sx={{ mb: 1.5 }}>
                                Device Kind
                            </Typography>
                            <Select
                                value={deviceKind}
                                onChange={(event, newValue) => setDeviceKind(newValue)}
                                size="md"
                                sx={{ width: '100%' }}
                            >
                                <Option value="hub-v1">Hub v1</Option>
                            </Select>
                        </Grid>
                        <Grid xs={12} sm={6}>
                            <Typography level="title-sm" sx={{ mb: 1.5 }}>
                                Approval Status
                            </Typography>
                            <Select
                                value={approvalStatus}
                                onChange={(event, newValue) => setApprovalStatus(newValue)}
                                size="md"
                                sx={{ width: '100%' }}
                            >
                                <Option value="all">All</Option>
                                <Option value="approved">Approved</Option>
                                <Option value="rejected">Rejected</Option>
                                <Option value="pending">Pending</Option>
                            </Select>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Firmware Releases Cards */}
            <Box sx={{ mb: 3 }}>
                <Typography level="h4" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                        width: 40, 
                        height: 40, 
                        borderRadius: '50%', 
                        bgcolor: 'danger.100', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        mr: 2 
                    }}>
                        <Memory sx={{ color: 'danger.600' }} />
                    </Box>
                    Firmware Releases
                    </Typography>
                    
                    {firmwareReleases.length === 0 ? (
                    <Card variant="soft" sx={{ p: 4, textAlign: 'center' }}>
                        <Memory sx={{ fontSize: 48, color: 'neutral.400', mb: 2 }} />
                        <Typography level="h4" sx={{ mb: 1 }}>No Firmware Releases Found</Typography>
                        <Typography level="body-md" textColor="neutral.500" sx={{ mb: 3 }}>
                            No firmware releases match your current filters. Try adjusting the filters above.
                                </Typography>
                        </Card>
                    ) : (
                    <Grid container spacing={4}>
                            {firmwareReleases.map((release) => (
                                <Grid key={release.id} xs={12} md={6} lg={4}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            boxShadow: 'md',
                                        }
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Stack spacing={2.5}>
                                                {/* Header */}
                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography level="h4" fontWeight="bold">
                                                        {release.version_name}
                                                    </Typography>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Chip
                                                        size="md"
                                                        startDecorator={getApprovalStatusIcon(release.approval_status)}
                                                        {...getApprovalStatusColor(release.approval_status)}
                                                    >
                                                        {release.approval_status}
                                                    </Chip>
                                                    
                                                    {/* Actions Menu */}
                                                    <Dropdown>
                                                        <MenuButton
                                                            slots={{ root: IconButton }}
                                                            slotProps={{ root: { variant: 'outlined', size: 'sm' } }}
                                                        >
                                                            <MoreVert />
                                                        </MenuButton>
                                                        <Menu>
                                                            {/* Conditional Approve/Reject Actions */}
                                                            {canModifyApproval(release) && (
                                                                <MenuItem
                                                                    onClick={() => handleApproveRelease(release)}
                                                                    sx={{ color: 'success.500' }}
                                                                >
                                                                    <ListItemDecorator>
                                                                        <Check />
                                                                    </ListItemDecorator>
                                                                    Approve
                                                                </MenuItem>
                                                            )}
                                                            
                                                            {canModifyApproval(release) && (
                                                                <MenuItem
                                                                    onClick={() => handleRejectRelease(release)}
                                                                    sx={{ color: 'danger.500' }}
                                                                >
                                                                    <ListItemDecorator>
                                                                        <Block />
                                                                    </ListItemDecorator>
                                                                    Reject
                                                                </MenuItem>
                                                            )}
                                                            
                                                            {canModifyApproval(release) && (
                                                                <MenuItem
                                                                    onClick={() => handlePendingRelease(release)}
                                                                    sx={{ color: 'warning.500' }}
                                                                >
                                                                    <ListItemDecorator>
                                                                        <Schedule />
                                                                    </ListItemDecorator>
                                                                    Pending
                                                                </MenuItem>
                                                            )}
                                                            
                                                            {canReassign(release) && (
                                                                <MenuItem
                                                                    onClick={() => handleOpenReassignModal(release)}
                                                                    sx={{ color: 'primary.500' }}
                                                                >
                                                                    <ListItemDecorator>
                                                                        <AssignmentInd />
                                                                    </ListItemDecorator>
                                                                    Re-assign
                                                                </MenuItem>
                                                            )}
                                                            
                                                            {/* Delete Action */}
                                                            <MenuItem
                                                                onClick={() => handleOpenDeleteModal(release)}
                                                                sx={{ color: 'danger.500' }}
                                                            >
                                                                <ListItemDecorator>
                                                                    <Delete />
                                                                </ListItemDecorator>
                                                                Delete
                                                            </MenuItem>
                                                        </Menu>
                                                    </Dropdown>
                                                </Stack>
                                                </Stack>

                                                {/* Device Kind & Release Type */}
                                                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                                <Typography level="title-sm" fontWeight="bold">
                                                        Device:
                                                    </Typography>
                                                <Chip size="md" variant="soft" color="neutral">
                                                        {release.device_kind}
                                                    </Chip>
                                                    {release.compose_file && (
                                                        <Chip
                                                            size="sm"
                                                            variant="soft"
                                                            color="primary"
                                                            startDecorator={<Layers sx={{ fontSize: 14 }} />}
                                                            onClick={() => setViewComposeRelease(release)}
                                                            sx={{ cursor: 'pointer' }}
                                                        >
                                                            Compose
                                                        </Chip>
                                                    )}
                                                </Stack>

                                                {/* Release Notes */}
                                                <Box>
                                                <Typography level="title-sm" fontWeight="bold" sx={{ mb: 1 }}>
                                                        Release Notes:
                                                    </Typography>
                                                    <Tooltip title="Show full release notes">
                                                        <Typography
                                                            level="body-sm"
                                                            color="neutral.600"
                                                            sx={{
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                minHeight: '2.8em', // Reserve space for 2 lines (adjust as needed for your font/lineHeight)
                                                                lineHeight: 1.4,
                                                                cursor: 'pointer',
                                                            }}
                                                            onClick={() => setOpenNotesId(release.id)}
                                                        >
                                                            {release.release_notes}
                                                        </Typography>
                                                    </Tooltip>
                                                    {/* Dialog for full release notes */}
                                                    <Modal open={openNotesId === release.id} onClose={() => setOpenNotesId(null)}>
                                                        <ModalDialog sx={{ maxWidth: 500 }}>
                                                            <DialogTitle>
                                                                Release Notes
                                                                <IconButton
                                                                    aria-label="close"
                                                                    onClick={() => setOpenNotesId(null)}
                                                                    sx={{ position: 'absolute', right: 8, top: 8 }}
                                                                >
                                                                    <Close />
                                                                </IconButton>
                                                            </DialogTitle>
                                                            <DialogContent>
                                                                <Typography level="body-md" sx={{ whiteSpace: 'pre-line' }}>
                                                                    {release.release_notes}
                                                                </Typography>
                                                            </DialogContent>
                                                        </ModalDialog>
                                                    </Modal>
                                                </Box>

                                                <Divider />

                                                {/* Metadata */}
                                            <Stack spacing={1.5}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                    <Typography level="body-sm" color="neutral.500">
                                                        Creator:
                                                    </Typography>
                                                    {release.creator ? (
                                                        <Tooltip title={release.creator.name || 'Waiting for login'}>
                                                            <Typography level="body-sm" fontWeight="md" sx={{ cursor: 'pointer' }}>
                                                                {release.creator.email}
                                                        </Typography>
                                                        </Tooltip>
                                                    ) : (
                                                        <Typography level="body-sm" fontWeight="md" color="neutral.500">
                                                            Unknown
                                                        </Typography>
                                                    )}
                                                    </Stack>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                    <Typography level="body-sm" color="neutral.500">
                                                            Approver:
                                                        </Typography>
                                                    {release.approver ? (
                                                        <Tooltip title={release.approver.name || 'Waiting for login'}>
                                                            <Typography level="body-sm" fontWeight="md" sx={{ cursor: 'pointer' }}>
                                                                {release.approver.email}
                                                            </Typography>
                                                        </Tooltip>
                                                    ) : (
                                                        <Typography level="body-sm" fontWeight="md" color="neutral.500">
                                                            -
                                                        </Typography>
                                                    )}
                                                    </Stack>
                                                    <Stack direction="row" justifyContent="space-between">
                                                    <Typography level="body-sm" color="neutral.500">
                                                            Created:
                                                        </Typography>
                                                    <Typography level="body-sm" fontWeight="md">
                                                            {formatDate(release.created_at)}
                                                        </Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
            </Box>

            {/* Delete Confirmation Modal */}
            <Modal open={deleteModalOpen} onClose={handleCloseDeleteModal}>
                <ModalDialog sx={{ minWidth: 400 }}>
                    <DialogTitle>
                        Confirm Delete
                        <IconButton
                            aria-label="close"
                            onClick={handleCloseDeleteModal}
                            sx={{ position: 'absolute', right: 8, top: 8 }}
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2}>
                            <Typography level="body-md">
                                Are you sure you want to delete this firmware release?
                            </Typography>
                            
                            {releaseToDelete && (
                                <Card variant="soft" sx={{ p: 2 }}>
                                    <Stack spacing={1}>
                                        <Typography level="title-md" fontWeight="bold">
                                            {releaseToDelete.version_name}
                                        </Typography>
                                        <Typography level="body-sm" textColor="neutral.600">
                                            Device: {releaseToDelete.device_kind}
                                        </Typography>
                                        <Typography level="body-sm" textColor="neutral.600">
                                            Status: {releaseToDelete.approval_status}
                                        </Typography>
                                    </Stack>
                                </Card>
                            )}

                            <Alert color="warning" variant="soft">
                                <Typography level="body-sm">
                                    <strong>Warning:</strong> This action cannot be undone. The firmware release will be permanently deleted.
                                </Typography>
                            </Alert>

                            {deleteError && (
                                <Alert color="danger" variant="soft">
                                    {deleteError}
                                </Alert>
                            )}

                            {deleteSuccess && (
                                <Alert color="success" variant="soft">
                                    {deleteSuccess}
                                </Alert>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="outlined"
                            onClick={handleCloseDeleteModal}
                            disabled={deleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="solid"
                            color="danger"
                            onClick={handleDeleteRelease}
                            loading={deleting}
                            disabled={deleting}
                            startDecorator={!deleting ? <Delete /> : null}
                        >
                            {deleting ? 'Deleting...' : 'Delete Release'}
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>

            {/* Re-assign Modal */}
            <Modal open={reassignModalOpen} onClose={handleCloseReassignModal}>
                <ModalDialog sx={{ minWidth: 500, maxWidth: 600 }}>
                    <DialogTitle>
                        Re-assign Approver
                        <IconButton
                            aria-label="close"
                            onClick={handleCloseReassignModal}
                            sx={{ position: 'absolute', right: 8, top: 8 }}
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={3}>
                            <Typography level="body-md">
                                Select a new approver for this firmware release:
                            </Typography>
                            
                            {releaseToReassign && (
                                <Card variant="soft" sx={{ p: 2 }}>
                                    <Stack spacing={1}>
                                        <Typography level="title-md" fontWeight="bold">
                                            {releaseToReassign.version_name}
                                        </Typography>
                                        <Typography level="body-sm" textColor="neutral.600">
                                            Device: {releaseToReassign.device_kind}
                                        </Typography>
                                        <Typography level="body-sm" textColor="neutral.600">
                                            Current Status: {releaseToReassign.approval_status}
                                        </Typography>
                                    </Stack>
                                </Card>
                            )}

                            {reassignError && (
                                <Alert color="danger" variant="soft">
                                    {reassignError}
                                </Alert>
                            )}

                            {loadingApprovers ? (
                                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                                    <Stack spacing={2} alignItems="center">
                                        <CircularProgress />
                                        <Typography level="body-sm">Loading approvers...</Typography>
                                    </Stack>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography level="title-sm" sx={{ mb: 2 }}>
                                        Available Approvers:
                                    </Typography>
                                    <Stack spacing={1}>
                                        {approvers.map((approver) => (
                                            <Card 
                                                key={approver.id}
                                                variant="outlined"
                                                sx={{ 
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        bgcolor: 'primary.50',
                                                        borderColor: 'primary.200'
                                                    },
                                                    opacity: reassigning ? 0.6 : 1,
                                                    pointerEvents: reassigning ? 'none' : 'auto'
                                                }}
                                                onClick={() => handleSelectApprover(approver.id)}
                                            >
                                                <CardContent sx={{ p: 2 }}>
                                                    <Stack direction="row" alignItems="center" spacing={2}>
                                                        <Box sx={{ 
                                                            width: 40, 
                                                            height: 40, 
                                                            borderRadius: '50%', 
                                                            bgcolor: 'primary.100', 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            justifyContent: 'center' 
                                                        }}>
                                                            <AssignmentInd sx={{ color: 'primary.600' }} />
                                                        </Box>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography level="title-sm">
                                                                {approver.name || 'Waiting for login'}
                                                            </Typography>
                                                            <Typography level="body-sm" textColor="neutral.500">
                                                                {approver.email}
                                                            </Typography>
                                                            {!approver.name && (
                                                                <Typography level="body-xs" textColor="warning.500">
                                                                    Name will appear after first login
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Stack spacing={1} alignItems="flex-end">
                                                            <Chip size="sm" variant="soft" color="primary">
                                                                ID: {approver.id}
                                                            </Chip>
                                                            {releaseToReassign && approver.id === releaseToReassign.approver?.id && (
                                                                <Chip size="sm" variant="soft" color="success">
                                                                    Current
                                                                </Chip>
                                                            )}
                                                        </Stack>
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="outlined"
                            onClick={handleCloseReassignModal}
                            disabled={reassigning}
                        >
                            Cancel
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>

            {/* Add Release Candidate Modal */}
            <Modal open={addModalOpen} onClose={handleCloseAddModal}>
                <ModalDialog size="md" sx={{ minWidth: 600 }}>
                    <DialogTitle>Add Release Candidate</DialogTitle>
                    <DialogContent sx={{ overflow: 'visible' }}>
                        <Stack spacing={3}>
                            {addError && (
                                <Alert color="danger" variant="soft">
                                    {addError}
                                </Alert>
                            )}
                            
                            {addSuccess && (
                                <Alert color="success" variant="soft">
                                    {addSuccess}
                                </Alert>
                            )}

                            <FormControl error={!!validationErrors.version}>
                                <FormLabel>Firmware Version *</FormLabel>
                                <Input
                                    placeholder="e.g., 2.1.0"
                                    value={newVersion}
                                    onChange={(e) => setNewVersion(e.target.value)}
                                    disabled={adding}
                                />
                                {validationErrors.version && (
                                    <Typography level="body-xs" color="danger">
                                        {validationErrors.version}
                                    </Typography>
                                )}
                            </FormControl>

                            <FormControl>
                                <FormLabel>Device Kind</FormLabel>
                                <Select
                                    value={newDeviceKind}
                                    onChange={(event, newValue) => setNewDeviceKind(newValue)}
                                    disabled={adding}
                                >
                                    <Option value="hub-v1">Hub v1</Option>
                                </Select>
                            </FormControl>

                            <FormControl error={!!validationErrors.releaseNotes}>
                                <FormLabel>Release Notes *</FormLabel>
                                <Textarea
                                    placeholder="Describe the changes in this release..."
                                    value={newReleaseNotes}
                                    onChange={(e) => setNewReleaseNotes(e.target.value)}
                                    minRows={4}
                                    maxRows={8}
                                    disabled={adding}
                                />
                                {validationErrors.releaseNotes && (
                                    <Typography level="body-xs" color="danger">
                                        {validationErrors.releaseNotes}
                                    </Typography>
                                )}
                            </FormControl>

                            <FormControl>
                                <FormLabel>Docker Compose File (YAML)</FormLabel>
                                <Textarea
                                    placeholder={"Paste your docker-compose.yml content here...\n\nservices:\n  bennu:\n    image: 547196422650.dkr.ecr.eu-central-1.amazonaws.com/ai-hub/bennu:2.0.0\n    ..."}
                                    value={newComposeFile}
                                    onChange={(e) => setNewComposeFile(e.target.value)}
                                    minRows={6}
                                    maxRows={12}
                                    disabled={adding}
                                    sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                                />
                                <Typography level="body-xs" textColor="neutral.500" sx={{ mt: 0.5 }}>
                                    Optional for legacy single-image releases. Required for multi-container compose releases.
                                </Typography>
                            </FormControl>

                            {newComposeFile.trim() && (() => {
                                const imageLines = newComposeFile.match(/image:\s*.+/g);
                                if (!imageLines || imageLines.length === 0) return null;
                                return (
                                    <Box sx={{ p: 1.5, bgcolor: 'neutral.50', borderRadius: 'sm', border: '1px solid', borderColor: 'neutral.200' }}>
                                        <Typography level="body-xs" fontWeight="bold" sx={{ mb: 1 }}>
                                            Detected images ({imageLines.length}):
                                        </Typography>
                                        <Stack spacing={0.5}>
                                            {imageLines.map((line, i) => (
                                                <Typography key={i} level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                                    {line.replace(/^\s*image:\s*/, '').trim()}
                                                </Typography>
                                            ))}
                                        </Stack>
                                    </Box>
                                );
                            })()}

                            <FormControl error={!!validationErrors.approver}>
                                <FormLabel>Approver *</FormLabel>
                                {loadingApprovers ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                                        <CircularProgress size="sm" />
                                        <Typography level="body-sm">Loading approvers...</Typography>
                                    </Box>
                                ) : (
                                    <Select
                                        placeholder="Select an approver"
                                        value={selectedApproverId}
                                        onChange={(event, newValue) => setSelectedApproverId(newValue)}
                                        disabled={adding}
                                    >
                                        {approvers.map((approver) => (
                                            <Option key={approver.id} value={approver.id.toString()}>
                                                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                                                    <Box sx={{ 
                                                        width: 24, 
                                                        height: 24, 
                                                        borderRadius: '50%', 
                                                        bgcolor: 'primary.100', 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'center' 
                                                    }}>
                                                        <AssignmentInd sx={{ fontSize: 14, color: 'primary.600' }} />
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography level="body-sm">
                                                            {approver.name || 'Waiting for login'}
                                                        </Typography>
                                                        <Typography level="body-xs" textColor="neutral.500">
                                                            {approver.email}
                                                        </Typography>
                                                    </Box>
                                                    <Chip size="sm" variant="soft" color="primary">
                                                        ID: {approver.id}
                                                    </Chip>
                                                </Stack>
                                            </Option>
                                        ))}
                                    </Select>
                                )}
                                {validationErrors.approver && (
                                    <Typography level="body-xs" color="danger">
                                        {validationErrors.approver}
                                    </Typography>
                                )}
                            </FormControl>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="outlined"
                            onClick={handleCloseAddModal}
                            disabled={adding}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="solid"
                            color="primary"
                            onClick={handleCreateReleaseCandidate}
                            loading={adding}
                            disabled={adding}
                        >
                            Create Release Candidate
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
            {/* View Compose File Modal */}
            <Modal open={!!viewComposeRelease} onClose={() => setViewComposeRelease(null)}>
                <ModalDialog sx={{ minWidth: 600, maxWidth: 800, maxHeight: '80vh' }}>
                    <DialogTitle>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Layers sx={{ color: 'primary.500' }} />
                            <Typography>Compose File — {viewComposeRelease?.version_name}</Typography>
                        </Stack>
                        <IconButton
                            aria-label="close"
                            onClick={() => setViewComposeRelease(null)}
                            sx={{ position: 'absolute', right: 8, top: 8 }}
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ overflow: 'auto' }}>
                        <Box
                            component="pre"
                            sx={{
                                p: 2,
                                bgcolor: 'neutral.50',
                                borderRadius: 'sm',
                                border: '1px solid',
                                borderColor: 'neutral.200',
                                fontFamily: 'monospace',
                                fontSize: '0.82rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                m: 0,
                            }}
                        >
                            {viewComposeRelease?.compose_file}
                        </Box>
                    </DialogContent>
                </ModalDialog>
            </Modal>
        </Sheet>
    );
};

export default FirmwarePage; 